import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  untracked,
} from '@angular/core';

import { CONTENT_KEYS } from '@core/models/content-keys';
import { BaseActionHandlerService, type ScopedActionHandler } from '@core/services/action-handler';
import { ContentService } from '@core/services/content.service';
import { LanguageService } from '@core/services/language.service';

import { PharmacyService } from '@features/pharmacy/services/pharmacy.service';

import { GroupsRenderer } from '@shared/components/groups-renderer/groups-renderer';
import type { ComponentGroup } from '@shared/models/dynamic-layout.types';
import type { DynamicAction } from '@shared/registry';
import { DataState } from '@shared/utils/data-state';

import type { PharmacyTaskCardConfig, PharmacyTaskCardContent } from './pharmacy-task-card.types';

@Component({
  selector: 'app-pharmacy-task-card',
  template: `
    <app-groups-renderer
      [groups]="groups()"
      (componentAction)="onAction($event)"
    />
  `,
  imports: [GroupsRenderer],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ContentService],
})
export class PharmacyTaskCard {
  private readonly pharmacyService = inject(PharmacyService);
  private readonly contentService = inject(ContentService<PharmacyTaskCardContent>);
  private readonly languageService = inject(LanguageService);
  private readonly baseActionHandler = inject(BaseActionHandlerService);

  readonly config = input<PharmacyTaskCardConfig>();

  private readonly pharmacyState = this.pharmacyService.state;

  private readonly combinedState = computed(() => DataState.combine(this.contentService.state(), this.pharmacyState()));

  private readonly rawContent = this.contentService.rawContent;

  private readonly jexlContext = computed<Record<string, unknown>>(() => {
    const status = this.combinedState().getSimpleStatus();
    const pharmacy = this.pharmacyService.pharmacy();

    return {
      status,
      pharmacy,
    };
  });

  protected readonly groups = computed<ComponentGroup[]>(() => {
    const content = this.rawContent();
    if (!content?.groups) return [];
    const groups = this.contentService.interpolateGroups(content.groups, this.jexlContext());
    return this.applyTitleOverrides(groups);
  });

  private readonly actionHandler: ScopedActionHandler = this.baseActionHandler.createScope({
    retry: () => this.pharmacyService.loadPharmacy(),
  });

  constructor() {
    effect(() => {
      this.languageService.currentLanguage();
      untracked(() => {
        this.contentService
          .loadFeatureContent(CONTENT_KEYS.COMPONENTS._id, CONTENT_KEYS.COMPONENTS.PHARMACY_TASK_CARD)
          .catch(console.error);
      });
    });

    afterNextRender(() => {
      const currentState = this.pharmacyState();
      if (!currentState.isSuccess() && !currentState.isLoading()) {
        this.pharmacyService.loadPharmacy();
      }
    });
  }

  protected onAction(event: DynamicAction): void {
    void this.actionHandler.execute(event);
  }

  private applyTitleOverrides(groups: ComponentGroup[]): ComponentGroup[] {
    const overrides = this.config();
    if (overrides?.title === undefined && overrides?.emptyTitle === undefined) return groups;

    return groups.map((group) => {
      if (group.id === 'pharmacy-populated' && overrides.title !== undefined) {
        return this.overrideTaskCardTitle(group, overrides.title);
      }
      if (group.id === 'pharmacy-empty' && overrides.emptyTitle !== undefined) {
        return this.overrideTaskCardTitle(group, overrides.emptyTitle);
      }
      return group;
    });
  }

  private overrideTaskCardTitle(group: ComponentGroup, title: string): ComponentGroup {
    return {
      ...group,
      items: group.items.map((item) => {
        if (item.itemType !== 'component') return item;
        const config = item.props['config'] as Record<string, unknown> | undefined;
        if (!config) return item;
        return {
          ...item,
          props: { ...item.props, config: { ...config, title } },
        };
      }),
    };
  }
}
